import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Switch, CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { formatPersianMoneyInput, parsePersianMoneyInput, amountToPersianWords } from '../utils/persianNumber';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import ArticleIcon from '@mui/icons-material/Article';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GppGoodIcon from '@mui/icons-material/GppGood';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import LockPersonIcon from '@mui/icons-material/LockPerson';

// Services & components
import settingService, { Setting, DigipayConnectionTest, SnappPayConnectionTest } from '../services/settingService';
import ElasticsearchSettingsPanel from '../components/settings/ElasticsearchSettingsPanel';
import RedisSettingsPanel from '../components/settings/RedisSettingsPanel';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

// ============================================================================
// Constants
// ============================================================================
const ELASTICSEARCH_TAB_KEY = '__elasticsearch__';
const REDIS_TAB_KEY = '__redis__';

// Money-like key/label detection for Persian formatting
const MONEY_HINTS = [
  'amount', 'price', 'money', 'fee', 'balance', 'commission',
  'withdraw', 'limit', 'مبلغ', 'کارمزد', 'موجودی', 'قیمت', 'هزینه'
];

// ============================================================================
// Helpers
// ============================================================================

const groupSettingsByCategory = (settings: Setting[]): Record<string, Setting[]> => {
  const groups: Record<string, Setting[]> = {};
  if (!settings || !Array.isArray(settings)) return groups;
  settings.forEach((setting) => {
    if (!setting.category) return;
    if (!groups[setting.category]) groups[setting.category] = [];
    groups[setting.category].push(setting);
  });
  return groups;
};

const getCategoryIcon = (category: string): JSX.Element => {
  switch (category) {
    case 'finance':
      return <MonetizationOnIcon fontSize="small" />;
    case 'account':
      return <SecurityIcon fontSize="small" />;
    case 'rewards':
      return <EmojiEventsIcon fontSize="small" />;
    case 'گارانتی و اسکرو':
      return <GppGoodIcon fontSize="small" />;
    case 'بازرسی':
      return <VerifiedUserIcon fontSize="small" />;
    case 'پرداخت':
    case 'درگاه دیجی‌پی':
    case 'درگاه اسنپ‌پی':
    case 'درگاه زرین‌پال':
      return <PaymentIcon fontSize="small" />;
    case 'تخفیف':
      return <LocalOfferIcon fontSize="small" />;
    case 'سبد خرید':
    case 'یادآوری سبد خرید':
      return <ShoppingCartIcon fontSize="small" />;
    case 'احراز هویت':
      return <LockPersonIcon fontSize="small" />;
    case 'قیمت‌گذاری دلاری':
      return <AttachMoneyIcon fontSize="small" />;
    case 'notifications':
    case 'اعلان‌ها':
      return <NotificationsIcon fontSize="small" />;
    case 'content':
    case 'محتوا':
      return <ArticleIcon fontSize="small" />;
    case 'support':
    case 'تیکت':
      return <SupportAgentIcon fontSize="small" />;
    case 'general':
    case 'عمومی':
      return <SettingsIcon fontSize="small" />;
    case ELASTICSEARCH_TAB_KEY:
      return <SpeedIcon fontSize="small" />;
    case REDIS_TAB_KEY:
      return <StorageIcon fontSize="small" />;
    default:
      return <AccountBalanceWalletIcon fontSize="small" />;
  }
};

const getCategoryLabel = (category: string): string => {
  switch (category) {
    case 'finance':
      return 'تنظیمات مالی';
    case 'account':
      return 'تنظیمات حساب کاربری';
    case 'rewards':
      return 'جوایز و پاداش‌ها';
    case 'گارانتی و اسکرو':
      return 'گارانتی و اسکرو';
    case 'بازرسی':
      return 'بازرسی محصول';
    case 'پرداخت':
      return 'تنظیمات پرداخت';
    case 'درگاه دیجی‌پی':
      return 'درگاه دیجی‌پی (اقساطی)';
    case 'درگاه اسنپ‌پی':
      return 'درگاه اسنپ‌پی (اقساطی)';
    case 'درگاه زرین‌پال':
      return 'درگاه زرین‌پال';
    case 'تخفیف':
      return 'کدهای تخفیف';
    case 'سبد خرید':
      return 'سبد خرید';
    case 'احراز هویت':
      return 'احراز هویت (ورود/ثبت‌نام)';
    case 'notifications':
      return 'اعلان‌ها';
    case 'content':
      return 'محتوا';
    case 'support':
      return 'تیکت و پشتیبانی';
    case 'general':
      return 'عمومی';
    case ELASTICSEARCH_TAB_KEY:
      return 'الستیک سرچ';
    case REDIS_TAB_KEY:
      return 'ردیس';
    default:
      return category;
  }
};

const isMoneyLikeSetting = (setting: Setting): boolean => {
  const haystack = `${setting.key} ${setting.label || ''}`.toLowerCase();
  return MONEY_HINTS.some((h) => haystack.includes(h.toLowerCase()));
};

const formatMoney = (val: number | string): string => {
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val ?? '');
  const withSep = n.toLocaleString('en-US');
  return toPersianDigits(withSep);
};

// For live formatting: format raw string with separator (kept as string, not Number)
const formatMoneyInput = (val: string | number): string => {
  const s = String(val ?? '').replace(/,/g, '').replace(/[^\d]/g, '');
  if (!s) return '';
  return toPersianDigits(s.replace(/\B(?=(\d{3})+(?!\d))/g, '٬'));
};

const parseMoneyInput = (val: string): string => {
  return val
    .replace(/[٬,]/g, '')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[^\d]/g, '');
};

const isBooleanSetting = (s: Setting): boolean => s.type === 'boolean';

// ============================================================================
// Scoped CSS (injected once)
// ============================================================================
const scopedCss = `
.settings-shell {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 20px;
  align-items: flex-start;
}
@media (max-width: 960px) {
  .settings-shell {
    grid-template-columns: 1fr;
  }
}

.settings-sidebar {
  background: var(--color-bg-paper);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 8px;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-shadow: var(--shadow-sm);
}
@media (max-width: 960px) {
  .settings-sidebar {
    position: static;
    max-height: none;
  }
  .settings-sidebar.collapsed .settings-nav-items { display: none; }
}

.settings-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 600;
  letter-spacing: .02em;
}
.settings-sidebar-header .m-hint {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-secondary);
}
@media (max-width: 960px) {
  .settings-sidebar-header .m-hint { display: inline-flex; }
}

.settings-nav-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.settings-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  transition: all .12s ease;
  border-right: 3px solid transparent;
}
.settings-nav-item:hover {
  background: var(--color-bg-hover);
}
.settings-nav-item.active {
  background: rgba(178, 83, 115, 0.08);
  color: #B25373;
  border-right-color: #B25373;
  font-weight: 600;
}
[data-theme="dark"] .settings-nav-item.active {
  background: rgba(178, 83, 115, 0.16);
}

.settings-nav-item .nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: inherit;
}

.settings-nav-item .nav-label {
  flex: 1;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.settings-nav-item .nav-count {
  display: inline-flex;
  min-width: 20px;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  background: var(--color-bg-hover);
  color: var(--color-text-secondary);
  justify-content: center;
}
.settings-nav-item.active .nav-count {
  background: #B25373;
  color: #fff;
}
.settings-nav-item .nav-count.modified {
  background: #f59e0b;
  color: #fff;
}

.settings-main {
  min-width: 0;
}

.settings-content-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--color-bg-paper);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: var(--shadow-sm);
}
.settings-content-head .head-titles h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-primary);
}
.settings-content-head .head-titles p {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--color-text-secondary);
}

.settings-search-row {
  background: var(--color-bg-paper);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 8px;
}
.settings-search-row .search-wrap {
  position: relative;
  flex: 1;
}
.settings-search-row .search-wrap input {
  width: 100%;
  padding: 9px 36px 9px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-default);
  color: var(--color-text-primary);
  font-size: 13px;
  outline: none;
  transition: all .12s;
}
.settings-search-row .search-wrap input:focus {
  border-color: #B25373;
  box-shadow: 0 0 0 3px rgba(178, 83, 115,0.12);
}
.settings-search-row .search-wrap .s-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-secondary);
  pointer-events: none;
  display: inline-flex;
}

.settings-section-card {
  background: var(--color-bg-paper);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 14px;
  overflow: hidden;
}
.settings-section-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--color-bg-default);
}
.settings-section-body {
  padding: 4px 0;
}

.settings-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
  gap: 20px;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border);
  position: relative;
  transition: background .12s;
}
.settings-row:last-child { border-bottom: none; }
.settings-row.modified {
  background: rgba(251, 191, 36, 0.06);
  border-right: 3px solid #f59e0b;
}
.settings-row:hover {
  background: var(--color-bg-hover);
}
.settings-row.modified:hover {
  background: rgba(251, 191, 36, 0.10);
}

@media (max-width: 720px) {
  .settings-row {
    grid-template-columns: 1fr;
  }
}

.settings-row .row-meta {
  min-width: 0;
}
.settings-row .row-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}
.settings-row .row-label .modified-dot {
  width: 7px;
  height: 7px;
  background: #f59e0b;
  border-radius: 50%;
  display: inline-block;
}
.settings-row .row-description {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--color-text-secondary);
}
.settings-row .row-key {
  margin-top: 6px;
  display: inline-block;
  font-family: monospace;
  font-size: 10px;
  color: var(--color-text-secondary);
  background: var(--color-bg-default);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 1px 6px;
}

.settings-row .row-control {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.settings-row .row-control .control-inputs { display: flex; gap: 6px; align-items: center; }
.settings-row .row-control .control-inputs > * { flex: 1; min-width: 0; }
.settings-row .row-control input[type="text"],
.settings-row .row-control input[type="number"],
.settings-row .row-control input[type="time"],
.settings-row .row-control select,
.settings-row .row-control textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-default);
  color: var(--color-text-primary);
  font-size: 13px;
  outline: none;
  font-family: inherit;
  transition: all .12s;
}
.settings-row .row-control input[type="number"] {
  direction: ltr;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.settings-row .row-control input:focus,
.settings-row .row-control select:focus,
.settings-row .row-control textarea:focus {
  border-color: #B25373;
  box-shadow: 0 0 0 3px rgba(178, 83, 115,0.12);
}
.settings-row .row-control textarea {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  resize: vertical;
  line-height: 1.5;
}
.settings-row .row-control select {
  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--color-text-secondary) 50%),
                    linear-gradient(135deg, var(--color-text-secondary) 50%, transparent 50%);
  background-position: calc(0% + 14px) center, calc(0% + 19px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-left: 32px;
}

.settings-row .row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  margin-top: 2px;
}
.settings-row .row-actions .icon-btn {
  border: 1px solid var(--color-border);
  background: var(--color-bg-paper);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all .12s;
}
.settings-row .row-actions .icon-btn:hover:not(:disabled) {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}
.settings-row .row-actions .icon-btn.save { color: #059669; border-color: #10b98144; }
.settings-row .row-actions .icon-btn.save:hover:not(:disabled) { background: #10b98112; }
.settings-row .row-actions .icon-btn.reset { color: #6b7280; }
.settings-row .row-actions .icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.settings-row .row-suffix {
  position: absolute;
  right: auto;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-secondary);
  font-size: 12px;
  pointer-events: none;
}

.settings-money-hint {
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.settings-switch-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}
.settings-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  gap: 16px;
  transition: background .12s;
}
.settings-switch-row:last-child { border-bottom: none; }
.settings-switch-row.modified {
  background: rgba(251, 191, 36, 0.06);
  border-right: 3px solid #f59e0b;
}
.settings-switch-row:hover { background: var(--color-bg-hover); }
.settings-switch-row.modified:hover { background: rgba(251, 191, 36, 0.10); }
.settings-switch-row .sw-meta { min-width: 0; flex: 1; }
.settings-switch-row .sw-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}
.settings-switch-row .sw-desc {
  margin-top: 2px;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}
.settings-switch-row .sw-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.settings-sub-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (max-width: 520px) {
  .settings-sub-grid { grid-template-columns: 1fr; }
}
.settings-sub-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.settings-sub-field label {
  font-size: 11px;
  color: var(--color-text-secondary);
}

.settings-day-card {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px;
  background: var(--color-bg-default);
}
.settings-day-card label.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--color-text-primary);
}
.settings-day-card .times {
  display: flex;
  gap: 6px;
}

.settings-skeleton {
  background: var(--color-bg-paper);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.settings-skeleton .sk-line {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--color-bg-default), var(--color-bg-hover), var(--color-bg-default));
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite;
}
.settings-skeleton .sk-line.sm { width: 30%; }
.settings-skeleton .sk-line.md { width: 60%; }
@keyframes sk-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.settings-modified-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}
[data-theme="dark"] .settings-modified-badge { color: #fbbf24; }

.settings-empty {
  padding: 48px 16px;
  text-align: center;
  color: var(--color-text-secondary);
}
.settings-empty .empty-icon {
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-hover);
  border-radius: 50%;
  margin-bottom: 12px;
}
`;

// ============================================================================
// Component
// ============================================================================

/** لینک مستقیم به تنظیمات هر درگاه: /settings?tab=snapppay|digipay|zarinpal */
const GATEWAY_TAB_KEYS: Record<string, string> = {
  snapppay: 'درگاه اسنپ‌پی',
  digipay: 'درگاه دیجی‌پی',
  zarinpal: 'درگاه زرین‌پال',
};

const SettingsManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saveLoading, setSaveLoading] = useState<Record<string, boolean>>({});
  const [savingAll, setSavingAll] = useState(false);
  // مبلغ آزمایشی پیش‌نمایش کمیسیون پلکانی
  const [tierTestAmount, setTierTestAmount] = useState<string>('150000000');
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  const [modifiedSettings, setModifiedSettings] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // --- Fetch settings ---
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await settingService.getAllSettings();
      if (!data || !Array.isArray(data)) {
        setSettings([]);
        setFormValues({});
        enqueueSnackbar('خطا در دریافت تنظیمات. داده‌های نامعتبر.', { variant: 'error' });
        return;
      }
      setSettings(data);
      const initial: Record<string, any> = {};
      data.forEach((s) => {
        initial[s.key] = s.value;
      });
      setFormValues(initial);
      setOriginalValues(initial);
      setModifiedSettings([]);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setSettings([]);
      setFormValues({});
      enqueueSnackbar('خطا در دریافت تنظیمات. لطفاً مجدداً تلاش کنید.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSettings();
    setRefreshing(false);
  };

  // --- Modification tracking ---
  const markModified = useCallback((key: string, newValue: any) => {
    setFormValues((prev) => ({ ...prev, [key]: newValue }));
    setModifiedSettings((prev) => {
      const isDifferent = JSON.stringify(newValue) !== JSON.stringify(originalValues[key]);
      if (isDifferent) {
        if (!prev.includes(key)) return [...prev, key];
        return prev;
      }
      return prev.filter((k) => k !== key);
    });
  }, [originalValues]);

  const handleInputChange = (key: string, rawValue: any, type: string) => {
    let parsedValue: any = rawValue;
    if (type === 'number') {
      parsedValue = rawValue === '' ? 0 : Number(rawValue);
    } else if (type === 'boolean') {
      parsedValue = Boolean(rawValue);
    }
    markModified(key, parsedValue);
  };

  const handleResetSetting = (key: string) => {
    setFormValues((prev) => ({ ...prev, [key]: originalValues[key] }));
    setModifiedSettings((prev) => prev.filter((k) => k !== key));
  };

  // --- Save ---
  const handleSave = async (key: string, value: any) => {
    setSaveLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const meta = settings.find((s) => s.key === key);
      let payload = value;
      if (meta?.type === 'object' && typeof value === 'string') {
        try { payload = JSON.parse(value); }
        catch {
          enqueueSnackbar('ساختار JSON نامعتبر است.', { variant: 'error' });
          return;
        }
      }
      const result = await settingService.updateSetting(key, payload);
      if (result) {
        setOriginalValues((prev) => ({ ...prev, [key]: value }));
        setModifiedSettings((prev) => prev.filter((k) => k !== key));
        enqueueSnackbar('تنظیمات با موفقیت ذخیره شد.', { variant: 'success' });
      } else {
        enqueueSnackbar('خطا در ذخیره تنظیمات. پاسخ نامعتبر.', { variant: 'error' });
      }
    } catch (err) {
      console.error(`Error saving setting ${key}:`, err);
      enqueueSnackbar('خطا در ذخیره تنظیمات.', { variant: 'error' });
    } finally {
      setSaveLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveAll = async () => {
    if (modifiedSettings.length === 0) return;
    setSavingAll(true);
    let successCount = 0;
    let failCount = 0;
    for (const key of [...modifiedSettings]) {
      try {
        const meta = settings.find((s) => s.key === key);
        let payload = formValues[key];
        if (meta?.type === 'object' && typeof payload === 'string') {
          try { payload = JSON.parse(payload); }
          catch { failCount++; continue; }
        }
        const result = await settingService.updateSetting(key, payload);
        if (result) {
          setOriginalValues((prev) => ({ ...prev, [key]: formValues[key] }));
          setModifiedSettings((prev) => prev.filter((k) => k !== key));
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        console.error(`Error saving ${key}`, e);
        failCount++;
      }
    }
    setSavingAll(false);
    enqueueSnackbar(
      failCount === 0
        ? `همه تغییرات (${toPersianDigits(String(successCount))}) با موفقیت ذخیره شد.`
        : `${toPersianDigits(String(successCount))} تغییر ذخیره، ${toPersianDigits(String(failCount))} مورد خطا.`,
      { variant: failCount === 0 ? 'success' : 'error' }
    );
  };

  // --- Categorization ---
  const filteredSettings = useMemo(() => {
    if (!searchTerm.trim()) return settings;
    const q = searchTerm.toLowerCase();
    return settings.filter(
      (s) =>
        s.key.toLowerCase().includes(q) ||
        (s.label || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
    );
  }, [settings, searchTerm]);

  const groupedSettings = useMemo(
    () => groupSettingsByCategory(filteredSettings),
    [filteredSettings]
  );

  const baseCategories = useMemo(() => Object.keys(groupedSettings), [groupedSettings]);
  const categories = useMemo(
    () => [...baseCategories, ELASTICSEARCH_TAB_KEY, REDIS_TAB_KEY],
    [baseCategories]
  );

  // Compute modified count per category
  const modifiedPerCategory = useMemo(() => {
    const map: Record<string, number> = {};
    settings.forEach((s) => {
      if (modifiedSettings.includes(s.key)) {
        map[s.category] = (map[s.category] || 0) + 1;
      }
    });
    return map;
  }, [settings, modifiedSettings]);

  // Default active category
  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0]);
    } else if (activeCategory && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0] || '');
    }
  }, [categories, activeCategory]);

  // Deep-link support (?tab=elasticsearch / ?tab=redis / ?tab=snapppay|digipay|zarinpal)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'elasticsearch' && categories.includes(ELASTICSEARCH_TAB_KEY)) {
      setActiveCategory(ELASTICSEARCH_TAB_KEY);
    } else if (tab === 'redis' && categories.includes(REDIS_TAB_KEY)) {
      setActiveCategory(REDIS_TAB_KEY);
    } else if (GATEWAY_TAB_KEYS[tab] && categories.includes(GATEWAY_TAB_KEYS[tab])) {
      // لینک مستقیم از «مرکز اسنپ‌پی/دیجی‌پی» به دستهٔ تنظیمات همان درگاه
      setActiveCategory(GATEWAY_TAB_KEYS[tab]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, categories.length]);

  const handleSelectCategory = (cat: string) => {
    setActiveCategory(cat);
    setMobileSidebarOpen(false);
    const params = new URLSearchParams(location.search);
    const gatewaySlug = Object.keys(GATEWAY_TAB_KEYS).find((k) => GATEWAY_TAB_KEYS[k] === cat);
    if (cat === ELASTICSEARCH_TAB_KEY) params.set('tab', 'elasticsearch');
    else if (cat === REDIS_TAB_KEY) params.set('tab', 'redis');
    else if (gatewaySlug) params.set('tab', gatewaySlug);
    else params.delete('tab');
    const nextSearch = params.toString();
    navigate(
      { pathname: '/settings', search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  };

  // ==========================================================================
  // Renderers
  // ==========================================================================

  const renderFieldInput = (setting: Setting): JSX.Element => {
    const { key, type, value } = setting;
    const currentValue = formValues[key] !== undefined ? formValues[key] : value;

    // Special: SLA response minutes
    if (key === 'support.sla.responseMinutesByPriority') {
      const obj =
        typeof currentValue === 'object' && currentValue
          ? currentValue
          : { low: 120, medium: 60, high: 30, urgent: 15 };
      const onChangePart = (field: string, v: string) => {
        const num = v === '' ? 0 : Number(v);
        const next = { ...(typeof formValues[key] === 'object' ? formValues[key] : obj), [field]: num };
        markModified(key, next);
      };
      return (
        <div className="settings-sub-grid">
          <div className="settings-sub-field"><label>کم (دقیقه)</label>
            <input type="number" value={obj.low ?? ''} onChange={(e) => onChangePart('low', e.target.value)} /></div>
          <div className="settings-sub-field"><label>متوسط (دقیقه)</label>
            <input type="number" value={obj.medium ?? ''} onChange={(e) => onChangePart('medium', e.target.value)} /></div>
          <div className="settings-sub-field"><label>زیاد (دقیقه)</label>
            <input type="number" value={obj.high ?? ''} onChange={(e) => onChangePart('high', e.target.value)} /></div>
          <div className="settings-sub-field"><label>فوری (دقیقه)</label>
            <input type="number" value={obj.urgent ?? ''} onChange={(e) => onChangePart('urgent', e.target.value)} /></div>
        </div>
      );
    }

    // Special: SLA resolution hours
    if (key === 'support.sla.resolutionHoursByPriority') {
      const obj =
        typeof currentValue === 'object' && currentValue
          ? currentValue
          : { low: 72, medium: 48, high: 24, urgent: 12 };
      const onChangePart = (field: string, v: string) => {
        const num = v === '' ? 0 : Number(v);
        const next = { ...(typeof formValues[key] === 'object' ? formValues[key] : obj), [field]: num };
        markModified(key, next);
      };
      return (
        <div className="settings-sub-grid">
          <div className="settings-sub-field"><label>کم (ساعت)</label>
            <input type="number" value={obj.low ?? ''} onChange={(e) => onChangePart('low', e.target.value)} /></div>
          <div className="settings-sub-field"><label>متوسط (ساعت)</label>
            <input type="number" value={obj.medium ?? ''} onChange={(e) => onChangePart('medium', e.target.value)} /></div>
          <div className="settings-sub-field"><label>زیاد (ساعت)</label>
            <input type="number" value={obj.high ?? ''} onChange={(e) => onChangePart('high', e.target.value)} /></div>
          <div className="settings-sub-field"><label>فوری (ساعت)</label>
            <input type="number" value={obj.urgent ?? ''} onChange={(e) => onChangePart('urgent', e.target.value)} /></div>
        </div>
      );
    }

    // Special: assignment strategy
    if (key === 'support.assignment.strategy') {
      const strategyValue = typeof currentValue === 'string' ? currentValue : 'least_load';
      return (
        <select value={strategyValue} onChange={(e) => handleInputChange(key, e.target.value, 'string')}>
          <option value="least_load">least_load</option>
          <option value="round_robin">round_robin</option>
        </select>
      );
    }

    // Special: auth primary channel (both | email | phone)
    if (key === 'auth_primary_channel') {
      const channelValue = typeof currentValue === 'string' && ['both', 'email', 'phone'].includes(currentValue)
        ? currentValue
        : 'both';
      return (
        <select value={channelValue} onChange={(e) => handleInputChange(key, e.target.value, 'string')}>
          <option value="both">هر دو (ایمیل و موبایل)</option>
          <option value="email">فقط ایمیل</option>
          <option value="phone">فقط موبایل</option>
        </select>
      );
    }

    // Special: Business hours
    if (key === 'support.businessHours') {
      const defaultVal: any = {
        timezone: 'Asia/Tehran',
        week: {
          sat: { enabled: true, start: '09:00', end: '18:00' },
          sun: { enabled: true, start: '09:00', end: '18:00' },
          mon: { enabled: true, start: '09:00', end: '18:00' },
          tue: { enabled: true, start: '09:00', end: '18:00' },
          wed: { enabled: true, start: '09:00', end: '18:00' },
          thu: { enabled: false, start: '00:00', end: '00:00' },
          fri: { enabled: false, start: '00:00', end: '00:00' }
        }
      };
      const obj = typeof currentValue === 'object' && currentValue ? currentValue : defaultVal;
      const setBH = (path: string[], v: any) => {
        const prevObj = typeof formValues[key] === 'object' && formValues[key] ? formValues[key] : obj;
        const next = JSON.parse(JSON.stringify(prevObj));
        let cursor: any = next;
        for (let i = 0; i < path.length - 1; i++) cursor = cursor[path[i]];
        cursor[path[path.length - 1]] = v;
        markModified(key, next);
      };
      const dayOrder = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'];
      const dayLabel: Record<string, string> = {
        sat: 'شنبه', sun: 'یکشنبه', mon: 'دوشنبه', tue: 'سه‌شنبه',
        wed: 'چهارشنبه', thu: 'پنجشنبه', fri: 'جمعه'
      };
      // Summary: count enabled days
      const enabledCount = dayOrder.filter((d) => obj.week?.[d]?.enabled).length;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, padding: '12px 14px',
            background: 'var(--color-bg-default)',
            border: '1px solid var(--color-divider)',
            borderRadius: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: enabledCount > 0 ? '#dcfce7' : '#fee2e2',
                color: enabledCount > 0 ? '#16a34a' : '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700,
              }}>
                {toPersianDigits(String(enabledCount))}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  {enabledCount} روز فعال از ۷
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  ساعات کاری پشتیبانی در هفته
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px',
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              fontSize: 11,
            }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>منطقه زمانی:</span>
              <input
                type="text"
                placeholder="Asia/Tehran"
                value={obj.timezone || ''}
                onChange={(e) => setBH(['timezone'], e.target.value)}
                style={{
                  border: 'none', background: 'transparent', outline: 'none',
                  fontSize: 12, fontFamily: 'monospace', direction: 'ltr',
                  color: 'var(--color-text-primary)', width: 110,
                }}
              />
            </div>
          </div>

          {/* Day cards grid — shift style */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 10,
          }}>
            {dayOrder.map((d) => {
              const dv = obj.week?.[d] || { enabled: false, start: '00:00', end: '00:00' };
              const enabled = !!dv.enabled;
              return (
                <div key={d} style={{
                  background: 'var(--color-bg-paper)',
                  border: `2px solid ${enabled ? '#B25373' : 'var(--color-divider)'}`,
                  borderRadius: 10,
                  padding: 10,
                  boxSizing: 'border-box',
                  transition: 'all 0.2s',
                  ...(enabled ? {} : { opacity: 0.7 }),
                }}>
                  {/* Day header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    paddingBottom: 8, marginBottom: 8,
                    borderBottom: '1px solid var(--color-divider)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 6,
                        background: enabled ? '#B25373' : 'var(--color-bg-default)',
                        color: enabled ? '#fff' : 'var(--color-text-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {dayLabel[d].charAt(0)}
                      </div>
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: enabled ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      }}>
                        {dayLabel[d]}
                      </div>
                    </div>
                    <Switch
                      size="small"
                      checked={enabled}
                      onChange={(e) => setBH(['week', d, 'enabled'], e.target.checked)}
                    />
                  </div>

                  {/* Time inputs */}
                  {enabled ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, direction: 'ltr' }}>
                      <input
                        type="time"
                        value={dv.start || ''}
                        onChange={(e) => setBH(['week', d, 'start'], e.target.value)}
                        style={{
                          flex: 1, minWidth: 0,
                          height: 34, padding: '0 6px',
                          border: '1px solid var(--color-border)', borderRadius: 6,
                          fontSize: 12, fontFamily: 'monospace', textAlign: 'center',
                          background: 'var(--color-bg-default)',
                          color: 'var(--color-text-primary)',
                          boxSizing: 'border-box', outline: 'none',
                        }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 600 }}>→</span>
                      <input
                        type="time"
                        value={dv.end || ''}
                        onChange={(e) => setBH(['week', d, 'end'], e.target.value)}
                        style={{
                          flex: 1, minWidth: 0,
                          height: 34, padding: '0 6px',
                          border: '1px solid var(--color-border)', borderRadius: 6,
                          fontSize: 12, fontFamily: 'monospace', textAlign: 'center',
                          background: 'var(--color-bg-default)',
                          color: 'var(--color-text-primary)',
                          boxSizing: 'border-box', outline: 'none',
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, color: 'var(--color-text-disabled)',
                      background: 'var(--color-bg-default)',
                      borderRadius: 6, border: '1px dashed var(--color-divider)',
                    }}>
                      تعطیل
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Number
    if (type === 'number') {
      // هر کلید درصدی (…_percentage / …_percent) پسوند ٪ می‌گیرد — قبلاً فقط دو کلید هاردکد بود
      const hasPercentSuffix =
        key === 'tax_vat_percentage' ||
        key === 'commission_percentage' ||
        /_(percentage|percent)$/.test(key);
      const isMoney = !hasPercentSuffix && isMoneyLikeSetting(setting);

      if (isMoney) {
        // Money input with live thousands separator in Persian digits
        const rawStr = String(currentValue ?? '');
        const displayVal = formatMoneyInput(rawStr);
        return (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              inputMode="numeric"
              value={displayVal}
              onChange={(e) => {
                const raw = parseMoneyInput(e.target.value);
                handleInputChange(key, raw, type);
              }}
              dir="ltr"
              style={{ textAlign: 'right', paddingLeft: 60, fontFamily: 'inherit' }}
            />
            <span
              className="row-suffix"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 12,
                color: 'var(--color-text-secondary)',
                pointerEvents: 'none',
                fontWeight: 500,
              }}
            >
              تومان
            </span>
          </div>
        );
      }

      return (
        <div style={{ position: 'relative' }}>
          <input
            type="number"
            value={currentValue ?? ''}
            onChange={(e) => handleInputChange(key, e.target.value, type)}
            style={hasPercentSuffix ? { paddingLeft: 30 } : undefined}
          />
          {hasPercentSuffix && <span className="row-suffix">%</span>}
        </div>
      );
    }

    // ادیتور اختصاصی پلکان کمیسیون (حاشیه‌ای) — به‌جای textarea خام JSON
    if (key === 'commission_tiers') {
      let tiers: Array<{ upTo: number | null; percent: number }> = [];
      try {
        const raw = typeof currentValue === 'string' ? JSON.parse(currentValue || '[]') : currentValue;
        if (Array.isArray(raw)) tiers = raw;
      } catch { /* مقدار خراب — از آرایه خالی شروع کن */ }

      const setTiers = (next: Array<{ upTo: number | null; percent: number }>) =>
        handleInputChange(key, next, type);

      // محاسبهٔ حاشیه‌ای + تفکیک: هر پله چقدر از مبلغ را گرفت و چه کارمزدی خورد
      const marginalFee = (
        amount: number
      ): { fee: number; rows: Array<{ index: number; from: number; to: number | null; percent: number; portion: number; fee: number }> } => {
        let fee = 0;
        let prevCap = 0;
        const rows: Array<{ index: number; from: number; to: number | null; percent: number; portion: number; fee: number }> = [];
        for (let ti = 0; ti < tiers.length; ti++) {
          const t = tiers[ti];
          if (amount <= prevCap) break;
          const cap = t.upTo === null || t.upTo === undefined ? Infinity : Number(t.upTo);
          const portion = Math.min(amount, cap) - prevCap;
          if (portion > 0) {
            const rowFee = Math.round(portion * (Number(t.percent) / 100));
            rows.push({
              index: ti + 1,
              from: prevCap,
              to: cap === Infinity ? null : Number(t.upTo),
              percent: Number(t.percent),
              portion,
              fee: rowFee
            });
            fee += rowFee;
          }
          if (cap === Infinity) break;
          prevCap = Number(t.upTo);
        }
        return { fee, rows };
      };

      return (
        <div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--color-text-primary)',
              marginBottom: 10,
              padding: 10,
              background: 'var(--color-info-bg, rgba(0,122,255,0.06))',
              borderRadius: 8,
              lineHeight: 1.9
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>📖 راهنما — چطور کمیسیون پلکانی تنظیم کنم؟</div>
            <div>۱. با دکمه‌ی «+ افزودن پله» به تعداد دلخواه پله بسازید.</div>
            <div>۲. برای هر پله، «تا سقف» را به تومان و «درصد» کارمزد آن بازه را وارد کنید.</div>
            <div>۳. سقف <strong>آخرین پله را خالی</strong> بگذارید — یعنی «هر مبلغی بالاتر از پله قبل».</div>
            <div style={{ marginTop: 6, fontWeight: 700 }}>مثال:</div>
            <div>پله ۱: تا سقف {toPersianDigits('20,000,000')} — درصد {toPersianDigits('5')}</div>
            <div>پله ۲: تا سقف {toPersianDigits('99,000,000')} — درصد {toPersianDigits('2')}</div>
            <div>پله ۳: سقف خالی (نامحدود) — درصد {toPersianDigits('0.5')}</div>
            <div style={{ marginTop: 6 }}>
              محاسبه <strong>حاشیه‌ای</strong> است؛ یعنی برای برداشت {toPersianDigits('150,000,000')} تومانی:
              {' '}{toPersianDigits('20')} میلیون اول با {toPersianDigits('5')}٪ + {toPersianDigits('79')} میلیون بعدی با {toPersianDigits('2')}٪ + {toPersianDigits('51')} میلیون آخر با {toPersianDigits('0.5')}٪
              → جمع کارمزد {toPersianDigits('2,835,000')} تومان (نه اینکه کل مبلغ یک نرخ بگیرد).
            </div>
            <div style={{ marginTop: 6, color: 'var(--color-text-secondary)' }}>
              • اگر هیچ پله‌ای تعریف نکنید، «درصد کمیسیون» ثابت (تنظیم جداگانه در همین صفحه) اعمال می‌شود.
              <br />• اگر فروشگاهی کمیسیون اختصاصی داشته باشد (صفحه‌ی «مدیریت فروشگاه‌ها» → تنظیم کمیسیون)، تنظیم اختصاصی او بر این پلکان سراسری مقدم است.
              <br />• نتیجه را در «پیش‌نمایش» پایین همین باکس، قبل از ذخیره ببینید.
            </div>
          </div>
          {tiers.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontSize: 12, minWidth: 40, paddingTop: 8 }}>پله {toPersianDigits(String(i + 1))}</span>
              <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  value={t.upTo === null || t.upTo === undefined ? '' : formatPersianMoneyInput(t.upTo)}
                  placeholder={i === tiers.length - 1 ? 'نامحدود' : 'تا سقف (تومان)'}
                  onChange={(e) => {
                    const raw = parsePersianMoneyInput(e.target.value);
                    const next = [...tiers];
                    next[i] = { ...next[i], upTo: raw === '' ? null : Number(raw) };
                    setTiers(next);
                  }}
                  style={{ textAlign: 'right' }}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                  {t.upTo === null || t.upTo === undefined
                    ? 'نامحدود — هر مبلغی بالاتر از پله قبل'
                    : amountToPersianWords(Number(t.upTo))}
                </span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input
                  type="number"
                  value={t.percent ?? ''}
                  placeholder="درصد"
                  onChange={(e) => {
                    const next = [...tiers];
                    next[i] = { ...next[i], percent: Number(e.target.value) };
                    setTiers(next);
                  }}
                  min={0}
                  max={100}
                  step={0.1}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                  {Number(t.percent) > 0 ? `${toPersianDigits(String(t.percent))} درصد کارمزد` : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setTiers(tiers.filter((_, idx) => idx !== i))}
                style={{
                  border: '1px solid var(--color-border)', borderRadius: 6, background: 'transparent',
                  color: 'var(--color-error-dark)', cursor: 'pointer', padding: '4px 10px', marginTop: 4
                }}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setTiers([...tiers, { upTo: null, percent: 0 }])}
            style={{
              border: '1px dashed var(--color-border)', borderRadius: 6, background: 'transparent',
              color: 'var(--color-text-primary)', cursor: 'pointer', padding: '6px 12px', fontSize: 12.5
            }}
          >
            + افزودن پله
          </button>

          {/* پیش‌نمایش با مبلغ آزمایشی + تفکیک پله‌به‌پله */}
          {tiers.length > 0 && (() => {
            const testAmount = Number(tierTestAmount) || 0;
            const { fee, rows } = marginalFee(testAmount);
            return (
              <div style={{ marginTop: 10, padding: 10, background: 'var(--color-bg-default)', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>🧮 پیش‌نمایش — یک مبلغ برداشت آزمایشی وارد کنید:</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    dir="ltr"
                    value={formatPersianMoneyInput(tierTestAmount)}
                    onChange={(e) => setTierTestAmount(parsePersianMoneyInput(e.target.value))}
                    style={{ width: 160, textAlign: 'right' }}
                  />
                  <span>تومان</span>
                  {[10000000, 50000000, 150000000].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setTierTestAmount(String(a))}
                      style={{
                        border: '1px solid var(--color-border)', borderRadius: 12, background: 'transparent',
                        color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '2px 10px', fontSize: 11
                      }}
                    >
                      {toPersianDigits((a / 1000000).toString())} میلیون
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                  {amountToPersianWords(testAmount)}
                </div>

                {testAmount > 0 && rows.length > 0 && (
                  <>
                    <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 6 }}>
                      {rows.map((r) => (
                        <div key={r.index} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                          <span>
                            پله {toPersianDigits(String(r.index))} —{' '}
                            {r.to === null
                              ? `مازاد بر ${formatPersianMoneyInput(r.from)}`
                              : r.from === 0
                                ? `تا ${formatPersianMoneyInput(r.to)}`
                                : `از ${formatPersianMoneyInput(r.from)} تا ${formatPersianMoneyInput(r.to)}`}
                            : {formatPersianMoneyInput(r.portion)} تومان × {toPersianDigits(String(r.percent))}٪
                          </span>
                          <strong>{formatPersianMoneyInput(r.fee)} تومان</strong>
                        </div>
                      ))}
                    </div>
                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>جمع کارمزد ({amountToPersianWords(fee)})</span>
                      <span>{formatPersianMoneyInput(fee)} تومان</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                      <span>خالص دریافتی فروشنده</span>
                      <strong>{formatPersianMoneyInput(testAmount - fee)} تومان</strong>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                      نرخ مؤثر: <strong>{toPersianDigits(((fee / testAmount) * 100).toFixed(2))}٪</strong> —
                      یعنی «میانگین وزنی» کارمزد روی کل مبلغ (جمع کارمزد ÷ کل مبلغ × {toPersianDigits('100')}).
                      چون پله‌های بالاتر نرخ کمتری دارند، این عدد از نرخ پله اول کوچک‌تر است و هرچه مبلغ بزرگ‌تر شود پایین‌تر می‌آید.
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      );
    }

    // ادیتور اختصاصی «مبنای محاسبهٔ کارمزد اقساطی» (دیجی‌پی / اسنپ‌پی) + پیش‌نمایش زنده روی یک سفارش نمونه
    if (key === 'digipay_surcharge_mode' || key === 'snapppay_surcharge_mode') {
      const isSnapp = key === 'snapppay_surcharge_mode';
      const gwName = isSnapp ? 'اسنپ‌پی' : 'دیجی‌پی';
      const rateKey = isSnapp ? 'snapppay_surcharge_percent' : 'digipay_credit_surcharge_percent';
      const mode = currentValue === 'gross_up' ? 'gross_up' : 'on_base';
      const rateRaw = (settings.find((s) => s.key === rateKey) as any)?.value;
      const rate = Number(formValues[rateKey] ?? rateRaw ?? 0) || 0;
      const sample = 1000000;
      const calc = (m: 'on_base' | 'gross_up') => {
        if (rate <= 0) return 0;
        if (m === 'gross_up') return rate >= 100 ? 0 : Math.round((sample * rate) / (100 - rate));
        return Math.round((sample * rate) / 100);
      };
      const fmtT = (n: number) => n.toLocaleString('fa-IR');
      const modeOptions = [
        { value: 'on_base', label: 'روی مبلغ سفارش (on_base)' },
        { value: 'gross_up', label: 'جبرانی — مبلغ سفارش کامل به ما برسد (gross_up)' }
      ];
      return (
        <div>
          <select
            value={mode}
            onChange={(e) => handleInputChange(key, e.target.value, type)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg-default)',
              color: 'var(--color-text-primary)', fontSize: 13
            }}
          >
            {modeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, padding: 10, background: 'var(--color-info-bg, rgba(0,122,255,0.06))', borderRadius: 8, fontSize: 12, lineHeight: 1.9 }}>
            پیش‌نمایش با کارمزد {fmtT(rate)}٪ روی سفارش {fmtT(sample)} تومانی:
            <br />
            • روی مبلغ سفارش: کارمزد <b>{fmtT(calc('on_base'))}</b> تومان → خریدار {fmtT(sample + calc('on_base'))} می‌پردازد؛ اگر {gwName} {fmtT(rate)}٪ را از کل مبلغ کم کند، به ما {fmtT(sample + calc('on_base') - Math.round(((sample + calc('on_base')) * rate) / 100))} می‌رسد.
            <br />
            • جبرانی: کارمزد <b>{fmtT(calc('gross_up'))}</b> تومان → خریدار {fmtT(sample + calc('gross_up'))} می‌پردازد؛ بعد از کسر {fmtT(rate)}٪ {gwName} از کل، دقیقاً {fmtT(sample)} به ما می‌رسد.
            <br />
            درصد و مبنای هر سفارش در لحظهٔ پرداخت روی همان سفارش ثبت می‌شود؛ تغییر این تنظیم سفارش‌های قبلی را تغییر نمی‌دهد.
          </div>
        </div>
      );
    }

    // ادیتور اختصاصی «مقصد استرداد سفارش‌های زرین‌پال»
    if (key === 'zarinpal_refund_channel') {
      const ch = currentValue === 'wallet' ? 'wallet' : 'gateway';
      const options = [
        { value: 'wallet', label: 'همیشه کیف پول کازماشاپ (پیش‌فرض فعلی — زرین‌پال استرداد API را غیرفعال کرده)' },
        { value: 'gateway', label: 'کارت بانکی خریدار از زرین‌پال (ریورس ≤۳۰ دقیقه / AddRefund) — در صورت رد، کیف پول' }
      ];
      return (
        <div>
          <select
            value={ch}
            onChange={(e) => handleInputChange(key, e.target.value, type)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg-default)',
              color: 'var(--color-text-primary)', fontSize: 13
            }}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, padding: 10, background: 'var(--color-info-bg, rgba(0,122,255,0.06))', borderRadius: 8, fontSize: 12, lineHeight: 1.9 }}>
            روی همهٔ استردادهای خودکار (لغو خریدار/فروشنده، داوری اختلاف، بازرسی، مرجوعی پستی، کرون‌های تلاش مجدد) اثر می‌گذارد.
            <br />
            • «کارت بانکی»: نیاز به ZP_ACCESS_TOKEN و ZP_TERMINAL_ID روی سرور و ثبت IP سرور در پنل زرین‌پال؛ اگر زرین‌پال همان لحظه نپذیرد (فعال نبودن، حداقل ۲٬۰۰۰ تومان، موجودی ناکافی) خودکار به کیف پول خریدار می‌رود و اگر استرداد ثبت‌شده بعداً ناموفق شود، کرون به کیف پول واریز می‌کند.
            <br />
            • ادمین در دیالوگ بازپرداخت هر سفارش زرین‌پال می‌تواند مقصد را دستی عوض کند. سفارش‌های اسنپ‌پی و دیجی‌پی همیشه از خودِ درگاه برمی‌گردند (کیف پول = استرداد دوبار).
          </div>
        </div>
      );
    }

    // ادیتور اختصاصی «حالت کمیسیون سراسری» — انتخاب صریح طرح فعال
    if (key === 'commission_mode') {
      const modeOptions = [
        { value: 'auto', label: 'خودکار — هر پیکربندی پرشده فعال است (پیشنهادی)' },
        { value: 'percent', label: 'درصد ثابت' },
        { value: 'percent_tiers', label: 'درصد پلکانی (حاشیه‌ای)' },
        { value: 'fixed', label: 'مبلغ ثابت هر برداشت' },
        { value: 'fixed_tiers', label: 'مبلغ ثابت پلکانی (بازه‌ای)' }
      ];
      return (
        <div>
          <select
            value={currentValue ?? 'auto'}
            onChange={(e) => handleInputChange(key, e.target.value, type)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg-default)',
              color: 'var(--color-text-primary)', fontSize: 13
            }}
          >
            {modeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, padding: 10, background: 'var(--color-info-bg, rgba(0,122,255,0.06))', borderRadius: 8, fontSize: 12, lineHeight: 1.9 }}>
            «خودکار»: هر پیکربندی‌ای که پر کرده باشید فعال می‌شود — اولویت:
            مبلغ‌ثابت پلکانی ← مبلغ ثابت (بزرگ‌تر از صفر) ← پلکان درصدی ← درصد ثابت.
            حالت‌های صریح، داده‌ی خودشان را می‌خوانند:
            «درصد ثابت» ← «درصد کمیسیون» | «درصد پلکانی» ← «کمیسیون پلکانی برداشت» |
            «مبلغ ثابت» ← «کمیسیون مبلغ ثابت هر برداشت» | «مبلغ ثابت پلکانی» ← «کمیسیون مبلغ‌ثابت پلکانی برداشت».
            اگر داده‌ی حالت انتخابی خالی/نامعتبر باشد، به‌صورت امن «درصد ثابت» اعمال می‌شود.
            کمیسیون اختصاصی فروشگاه‌ها همیشه مقدم است.
          </div>
        </div>
      );
    }

    // ادیتور اختصاصی پلکان مبلغ‌ثابت کمیسیون (bracket) — به‌جای textarea خام JSON
    if (key === 'commission_fixed_tiers') {
      let fixedTiers: Array<{ upTo: number | null; amount: number }> = [];
      try {
        const raw = typeof currentValue === 'string' ? JSON.parse(currentValue || '[]') : currentValue;
        if (Array.isArray(raw)) fixedTiers = raw;
      } catch { /* مقدار خراب — از آرایه خالی شروع کن */ }

      const setFixedTiers = (next: Array<{ upTo: number | null; amount: number }>) =>
        handleInputChange(key, next, type);

      // پیدا کردن پلهٔ منطبق برای مبلغ آزمایشی (bracket)
      const bracketFee = (
        amount: number
      ): { fee: number; bracket: { index: number; from: number; to: number | null; amount: number } | null } => {
        if (amount <= 0 || fixedTiers.length === 0) return { fee: 0, bracket: null };
        let prevCap = 0;
        for (let ti = 0; ti < fixedTiers.length; ti++) {
          const t = fixedTiers[ti];
          const cap = t.upTo === null || t.upTo === undefined ? null : Number(t.upTo);
          if (cap === null || amount <= cap) {
            const fee = Math.min(Math.max(0, Math.round(Number(t.amount) || 0)), amount);
            return { fee, bracket: { index: ti + 1, from: prevCap, to: cap, amount: Number(t.amount) || 0 } };
          }
          prevCap = cap;
        }
        const last = fixedTiers[fixedTiers.length - 1];
        const fee = Math.min(Math.max(0, Math.round(Number(last.amount) || 0)), amount);
        return { fee, bracket: { index: fixedTiers.length, from: prevCap, to: null, amount: Number(last.amount) || 0 } };
      };

      return (
        <div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--color-text-primary)',
              marginBottom: 10,
              padding: 10,
              background: 'var(--color-info-bg, rgba(0,122,255,0.06))',
              borderRadius: 8,
              lineHeight: 1.9
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>📖 راهنما — کمیسیون مبلغ‌ثابت پلکانی</div>
            <div>۱. با «+ افزودن پله» پله بسازید؛ برای هر پله «تا سقف» (تومان) و «مبلغ ثابت کارمزد» (تومان) را وارد کنید.</div>
            <div>۲. سقف <strong>آخرین پله را خالی</strong> بگذارید (نامحدود).</div>
            <div style={{ marginTop: 6 }}>
              محاسبه <strong>بازه‌ای (bracket)</strong> است: کل مبلغ برداشت در پله‌ی خودش می‌افتد و مبلغ ثابتِ همان پله کسر می‌شود
              — مثال: تا ۵ میلیون → ۱۰۰ هزار | تا ۲۰ میلیون → ۲۵۰ هزار | نامحدود → ۵۰۰ هزار؛ برداشت ۸ میلیونی = ۲۵۰ هزار تومان.
            </div>
            <div style={{ marginTop: 6, color: 'var(--color-text-secondary)' }}>
              • این تنظیم وقتی «حالت کمیسیون سراسری» روی «مبلغ ثابت پلکانی» یا «خودکار» باشد (و این پلکان پر باشد) اعمال می‌شود.
              <br />• ⚠️ در مرز پله‌ها کارمزد «پرش» دارد (۵٬۰۰۰٬۰۰۰ → ۱۰۰ هزار ولی ۵٬۰۰۰٬۰۰۱ → ۲۵۰ هزار).
              <br />• کارمزد هرگز از بخش مشمول برداشت بیشتر نمی‌شود؛ بخش معاف بازرسی مشمول نیست.
            </div>
          </div>
          {fixedTiers.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontSize: 12, minWidth: 40, paddingTop: 8 }}>پله {toPersianDigits(String(i + 1))}</span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  value={t.upTo === null || t.upTo === undefined ? '' : formatPersianMoneyInput(t.upTo)}
                  placeholder={i === fixedTiers.length - 1 ? 'نامحدود' : 'تا سقف (تومان)'}
                  onChange={(e) => {
                    const raw = parsePersianMoneyInput(e.target.value);
                    const next = [...fixedTiers];
                    next[i] = { ...next[i], upTo: raw === '' ? null : Number(raw) };
                    setFixedTiers(next);
                  }}
                  style={{ textAlign: 'right' }}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                  {t.upTo === null || t.upTo === undefined
                    ? 'نامحدود — هر مبلغی بالاتر از پله قبل'
                    : amountToPersianWords(Number(t.upTo))}
                </span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  value={t.amount === null || t.amount === undefined ? '' : formatPersianMoneyInput(t.amount)}
                  placeholder="کارمزد ثابت (تومان)"
                  onChange={(e) => {
                    const raw = parsePersianMoneyInput(e.target.value);
                    const next = [...fixedTiers];
                    next[i] = { ...next[i], amount: raw === '' ? 0 : Number(raw) };
                    setFixedTiers(next);
                  }}
                  style={{ textAlign: 'right' }}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                  {Number(t.amount) > 0 ? amountToPersianWords(Number(t.amount)) : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setFixedTiers(fixedTiers.filter((_, idx) => idx !== i))}
                style={{
                  border: '1px solid var(--color-border)', borderRadius: 6, background: 'transparent',
                  color: 'var(--color-error-dark)', cursor: 'pointer', padding: '4px 10px', marginTop: 4
                }}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFixedTiers([...fixedTiers, { upTo: null, amount: 0 }])}
            style={{
              border: '1px dashed var(--color-border)', borderRadius: 6, background: 'transparent',
              color: 'var(--color-text-primary)', cursor: 'pointer', padding: '6px 12px', fontSize: 12.5
            }}
          >
            + افزودن پله
          </button>

          {/* پیش‌نمایش با مبلغ آزمایشی — پلهٔ منطبق + هشدارها */}
          {fixedTiers.length > 0 && (() => {
            const testAmount = Number(tierTestAmount) || 0;
            const { fee, bracket } = bracketFee(testAmount);
            return (
              <div style={{ marginTop: 10, padding: 10, background: 'var(--color-bg-default)', borderRadius: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>🧮 پیش‌نمایش — یک مبلغ برداشت آزمایشی وارد کنید:</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    dir="ltr"
                    value={formatPersianMoneyInput(tierTestAmount)}
                    onChange={(e) => setTierTestAmount(parsePersianMoneyInput(e.target.value))}
                    style={{ width: 160, textAlign: 'right' }}
                  />
                  <span>تومان</span>
                  {[1000000, 10000000, 50000000].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setTierTestAmount(String(a))}
                      style={{
                        border: '1px solid var(--color-border)', borderRadius: 12, background: 'transparent',
                        color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '2px 10px', fontSize: 11
                      }}
                    >
                      {toPersianDigits((a / 1000000).toString())} میلیون
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                  {amountToPersianWords(testAmount)}
                </div>

                {testAmount > 0 && bracket && (
                  <>
                    <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                      <span>
                        پله {toPersianDigits(String(bracket.index))} —{' '}
                        {bracket.to === null
                          ? `مازاد بر ${formatPersianMoneyInput(bracket.from)}`
                          : bracket.from === 0
                            ? `تا ${formatPersianMoneyInput(bracket.to)}`
                            : `از ${formatPersianMoneyInput(bracket.from)} تا ${formatPersianMoneyInput(bracket.to)}`}
                        : مبلغ ثابت {formatPersianMoneyInput(bracket.amount)}
                      </span>
                      <strong>{formatPersianMoneyInput(fee)} تومان</strong>
                    </div>
                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>کارمزد ({amountToPersianWords(fee)})</span>
                      <span>{formatPersianMoneyInput(fee)} تومان</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                      <span>خالص دریافتی فروشنده</span>
                      <strong>{formatPersianMoneyInput(testAmount - fee)} تومان</strong>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                      نرخ مؤثر: <strong>{toPersianDigits(((fee / testAmount) * 100).toFixed(2))}٪</strong> از مبلغ برداشت.
                    </div>
                    {bracket.amount >= testAmount && (
                      <div style={{ marginTop: 6, padding: 8, background: 'var(--color-warning-bg, #fff8e1)', borderRadius: 6, fontSize: 11.5, lineHeight: 1.8 }}>
                        ⚠️ مبلغ کارمزد این پله ({formatPersianMoneyInput(bracket.amount)}) از خود مبلغ برداشت
                        بیشتر/مساوی است — عملاً کل بخش مشمول کسر می‌شود.
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      );
    }

    // Object (JSON)
    if (type === 'object') {
      const jsonText = typeof currentValue === 'string'
        ? currentValue
        : JSON.stringify(currentValue ?? {}, null, 2);
      return (
        <textarea
          value={jsonText}
          rows={6}
          onChange={(e) => handleInputChange(key, e.target.value, type)}
        />
      );
    }

    // Default text
    return (
      <input
        type="text"
        value={currentValue ?? ''}
        onChange={(e) => handleInputChange(key, e.target.value, type)}
      />
    );
  };

  const renderSettingRow = (setting: Setting) => {
    const isModified = modifiedSettings.includes(setting.key);
    const isSaving = !!saveLoading[setting.key];
    const disabledSave =
      JSON.stringify(formValues[setting.key]) === JSON.stringify(originalValues[setting.key]);

    return (
      <div key={setting.key} className={`settings-row ${isModified ? 'modified' : ''}`}>
        <div className="row-meta">
          <div className="row-label">
            {isModified && <span className="modified-dot" />}
            <span>{setting.label || setting.key}</span>
          </div>
          {setting.description && (
            <div className="row-description">{setting.description}</div>
          )}
          <span className="row-key">{setting.key}</span>
        </div>
        <div className="row-control">
          <div className="control-inputs">{renderFieldInput(setting)}</div>
          {isModified && (
            <div className="row-actions">
              <Tooltip title="ذخیره این مورد">
                <span>
                  <button
                    type="button"
                    className="icon-btn save"
                    onClick={() => handleSave(setting.key, formValues[setting.key])}
                    disabled={isSaving || disabledSave}
                  >
                    {isSaving ? <CircularProgress size={14} /> : <SaveIcon style={{ fontSize: 16 }} />}
                  </button>
                </span>
              </Tooltip>
              <Tooltip title="بازنشانی">
                <button
                  type="button"
                  className="icon-btn reset"
                  onClick={() => handleResetSetting(setting.key)}
                >
                  <RestoreIcon style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSwitchRow = (setting: Setting) => {
    const currentValue =
      formValues[setting.key] !== undefined ? formValues[setting.key] : setting.value;
    const isModified = modifiedSettings.includes(setting.key);
    const isSaving = !!saveLoading[setting.key];
    return (
      <div key={setting.key} className={`settings-switch-row ${isModified ? 'modified' : ''}`}>
        <div className="sw-meta">
          <div className="sw-label">
            {isModified && <span className="modified-dot" />}
            <span>{setting.label || setting.key}</span>
          </div>
          {setting.description && <div className="sw-desc">{setting.description}</div>}
          <span className="row-key">{setting.key}</span>
        </div>
        <div className="sw-actions">
          {isModified && (
            <>
              <Tooltip title="بازنشانی">
                <button
                  type="button"
                  className="icon-btn reset"
                  onClick={() => handleResetSetting(setting.key)}
                  style={{
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-paper)',
                    borderRadius: 6,
                    width: 28,
                    height: 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <RestoreIcon style={{ fontSize: 16, color: '#6b7280' }} />
                </button>
              </Tooltip>
              <Tooltip title="ذخیره این مورد">
                <span>
                  <button
                    type="button"
                    onClick={() => handleSave(setting.key, formValues[setting.key])}
                    disabled={isSaving}
                    style={{
                      border: '1px solid #10b98144',
                      background: 'var(--color-bg-paper)',
                      color: '#059669',
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.5 : 1
                    }}
                  >
                    {isSaving ? <CircularProgress size={14} /> : <SaveIcon style={{ fontSize: 16 }} />}
                  </button>
                </span>
              </Tooltip>
            </>
          )}
          <Switch
            size="small"
            checked={!!currentValue}
            onChange={(e) => handleInputChange(setting.key, e.target.checked, 'boolean')}
          />
        </div>
      </div>
    );
  };

  // ==========================================================================
  // Computed: current category settings
  // ==========================================================================
  const isEsTab = activeCategory === ELASTICSEARCH_TAB_KEY;
  const isRedisTab = activeCategory === REDIS_TAB_KEY;
  // ── تست اتصال دیجی‌پی (فقط در تب «درگاه دیجی‌پی») ──
  const isDigipayTab = activeCategory === 'درگاه دیجی‌پی';
  const [dpTest, setDpTest] = useState<DigipayConnectionTest | null>(null);
  const [dpTesting, setDpTesting] = useState(false);
  const runDigipayTest = async () => {
    setDpTesting(true);
    setDpTest(null);
    try {
      const r = await settingService.testDigipayConnection();
      setDpTest(r);
    } catch (e: any) {
      setDpTest({
        apiBase: '', host: '', configured: false,
        dns: { ok: false, addresses: [] }, login: { ok: false, ms: 0, error: e?.response?.data?.message || e?.message || 'خطا' },
        hint: 'اجرای تست ناموفق بود',
      });
    } finally {
      setDpTesting(false);
    }
  };
  // ── تست اتصال اسنپ‌پی (فقط در تب «درگاه اسنپ‌پی») ──
  const isSnappPayTab = activeCategory === 'درگاه اسنپ‌پی';
  const [spTest, setSpTest] = useState<SnappPayConnectionTest | null>(null);
  const [spTesting, setSpTesting] = useState(false);
  const runSnappPayTest = async () => {
    setSpTesting(true);
    setSpTest(null);
    try {
      const r = await settingService.testSnappPayConnection();
      setSpTest(r);
    } catch (e: any) {
      setSpTest({
        apiBase: '', host: '', configured: false,
        dns: { ok: false, addresses: [] }, login: { ok: false, ms: 0, error: e?.response?.data?.message || e?.message || 'خطا' },
        eligible: { ok: false },
        hint: 'اجرای تست ناموفق بود',
      });
    } finally {
      setSpTesting(false);
    }
  };
  const currentCategorySettings =
    !isEsTab && !isRedisTab ? (groupedSettings[activeCategory] || []) : [];
  const booleanSettings = currentCategorySettings.filter(isBooleanSetting);
  const nonBooleanSettings = currentCategorySettings.filter((s) => !isBooleanSetting(s));
  const modifiedCount = modifiedSettings.length;

  return (
    <div className="entity-page">
      <style>{scopedCss}</style>

      {/* ===================== Header ===================== */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تنظیمات سیستم</h1>
          <p className="entity-subtitle">مدیریت تنظیمات و پیکربندی پلتفرم</p>
        </div>
        <div className="header-actions">
          {modifiedCount > 0 && (
            <span className="settings-modified-badge">
              <InfoOutlinedIcon style={{ fontSize: 14 }} />
              {toPersianDigits(String(modifiedCount))} تغییر ذخیره‌نشده
            </span>
          )}
          <button
            className="header-btn refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            title="بروزرسانی"
          >
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </button>
          <button
            className="header-btn primary"
            onClick={handleSaveAll}
            disabled={savingAll || modifiedCount === 0}
            title="ذخیره همه تغییرات"
          >
            {savingAll ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <SaveIcon fontSize="small" />}
            ذخیره تغییرات
            {modifiedCount > 0 ? ` (${toPersianDigits(String(modifiedCount))})` : ''}
          </button>
        </div>
      </div>

      {/* ===================== Body ===================== */}
      {loading ? (
        <div className="settings-skeleton">
          <div className="sk-line sm" />
          <div className="sk-line md" />
          <div className="sk-line" />
          <div className="sk-line md" />
          <div className="sk-line sm" />
        </div>
      ) : (
        <div className="settings-shell">
          {/* ===================== Sidebar ===================== */}
          <aside className={`settings-sidebar ${mobileSidebarOpen ? '' : 'collapsed'}`}>
            <div className="settings-sidebar-header">
              <span>دسته‌بندی‌ها</span>
              <button
                type="button"
                className="m-hint"
                onClick={() => setMobileSidebarOpen((v) => !v)}
                title="باز/بسته"
              >
                <MenuIcon fontSize="small" />
              </button>
            </div>
            <div className="settings-nav-items">
              {categories.map((cat) => {
                const count = groupedSettings[cat]?.length || 0;
                const modCount = modifiedPerCategory[cat] || 0;
                const isActive = cat === activeCategory;
                const isMeta = cat === ELASTICSEARCH_TAB_KEY || cat === REDIS_TAB_KEY;
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`settings-nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleSelectCategory(cat)}
                  >
                    <span className="nav-icon">{getCategoryIcon(cat)}</span>
                    <span className="nav-label">{getCategoryLabel(cat)}</span>
                    {!isMeta && (
                      modCount > 0 ? (
                        <span className="nav-count modified" title="تعداد تغییرات ذخیره‌نشده">
                          {toPersianDigits(String(modCount))}
                        </span>
                      ) : (
                        <span className="nav-count">{toPersianDigits(String(count))}</span>
                      )
                    )}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ===================== Main ===================== */}
          <section className="settings-main">
            {/* Content head */}
            <div className="settings-content-head">
              <div className="head-titles">
                <h2>
                  {getCategoryIcon(activeCategory)}
                  {getCategoryLabel(activeCategory || '')}
                </h2>
                {!isEsTab && !isRedisTab && (
                  <p>
                    {toPersianDigits(String(currentCategorySettings.length))} تنظیمات در این دسته
                    {modifiedPerCategory[activeCategory]
                      ? ` — ${toPersianDigits(String(modifiedPerCategory[activeCategory]))} تغییر ذخیره‌نشده`
                      : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Search (not for ES/Redis tabs) */}
            {!isEsTab && !isRedisTab && (
              <div className="settings-search-row">
                <div className="search-wrap">
                  <input
                    type="text"
                    placeholder="جستجو در تنظیمات (کلید، نام یا توضیحات)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="s-icon"><SearchIcon fontSize="small" /></span>
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    className="header-btn"
                    onClick={() => setSearchTerm('')}
                  >
                    <ClearIcon fontSize="small" />
                    پاک
                  </button>
                )}
              </div>
            )}

            {/* تست اتصال دیجی‌پی — DNS + لاگین، بدون تراکنش */}
            {isDigipayTab && (
              <div className="settings-section-card" style={{ marginBottom: 12 }}>
                <div className="settings-section-header">
                  <PaymentIcon fontSize="small" />
                  تست اتصال به دیجی‌پی
                </div>
                <div style={{ padding: 14, fontSize: 13, lineHeight: 1.9 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="header-btn" onClick={runDigipayTest} disabled={dpTesting}>
                      {dpTesting ? 'در حال تست…' : 'تست اتصال (DNS + لاگین)'}
                    </button>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
                      اعتبارنامه‌ها از env سرور خوانده می‌شوند (DIGIPAY_*)؛ این تست هیچ تراکنشی نمی‌زند.
                    </span>
                  </div>
                  {dpTest && (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: dpTest.login.ok ? 'var(--color-success-bg, #e8f5e9)' : 'var(--color-error-bg, #fdecea)' }}>
                      <div><b>آدرس API:</b> <span dir="ltr">{dpTest.apiBase || '—'}</span> {dpTest.amountUnit ? `(واحد مبلغ: ${dpTest.amountUnit})` : ''}</div>
                      <div><b>پیکربندی:</b> {dpTest.configured ? 'کامل' : 'ناقص'}{dpTest.clientIdHasNonAscii ? ' — ⚠️ client_id کاراکتر غیرانگلیسی دارد' : ''}</div>
                      <div><b>DNS:</b> {dpTest.dns.ok ? `OK (${dpTest.dns.addresses.join(', ')})` : `ناموفق ${dpTest.dns.error ? `— ${dpTest.dns.error}` : ''}`}</div>
                      <div><b>لاگین:</b> {dpTest.login.ok ? `موفق (${toPersianDigits(String(dpTest.login.ms))} ms)` : `ناموفق ${dpTest.login.error ? `— ${dpTest.login.error}` : ''}`}</div>
                      <div style={{ marginTop: 6, fontWeight: 600 }}>{dpTest.hint}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* تست اتصال اسنپ‌پی — DNS + لاگین + eligible، بدون تراکنش */}
            {isSnappPayTab && (
              <div className="settings-section-card" style={{ marginBottom: 12 }}>
                <div className="settings-section-header">
                  <PaymentIcon fontSize="small" />
                  تست اتصال به اسنپ‌پی
                </div>
                <div style={{ padding: 14, fontSize: 13, lineHeight: 1.9 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="header-btn" onClick={runSnappPayTest} disabled={spTesting}>
                      {spTesting ? 'در حال تست…' : 'تست اتصال (DNS + لاگین + eligible)'}
                    </button>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
                      اعتبارنامه‌ها از env سرور خوانده می‌شوند (SNAPPPAY_*)؛ IP سرور باید نزد اسنپ‌پی وایت‌لیست باشد. این تست هیچ تراکنشی نمی‌زند.
                    </span>
                  </div>
                  {spTest && (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: spTest.login.ok && spTest.eligible.ok ? 'var(--color-success-bg, #e8f5e9)' : 'var(--color-error-bg, #fdecea)' }}>
                      <div><b>آدرس API:</b> <span dir="ltr">{spTest.apiBase || '—'}</span></div>
                      <div><b>پیکربندی:</b> {spTest.configured ? 'کامل' : 'ناقص'}</div>
                      <div><b>DNS:</b> {spTest.dns.ok ? `OK (${spTest.dns.addresses.join(', ')})` : `ناموفق ${spTest.dns.error ? `— ${spTest.dns.error}` : ''}`}</div>
                      <div><b>لاگین:</b> {spTest.login.ok ? `موفق (${toPersianDigits(String(spTest.login.ms))} ms)` : `ناموفق ${spTest.login.error ? `— ${spTest.login.error}` : ''}`}</div>
                      <div><b>استعلام eligible (۱٬۰۰۰٬۰۰۰ تومان):</b> {spTest.eligible.ok ? `${spTest.eligible.eligible ? 'مجاز' : 'غیرمجاز'}${spTest.eligible.title ? ` — «${spTest.eligible.title}» / «${spTest.eligible.description || ''}»` : ''}` : `ناموفق ${spTest.eligible.error ? `— ${spTest.eligible.error}` : ''}`}</div>
                      <div style={{ marginTop: 6, fontWeight: 600 }}>{spTest.hint}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Special panels */}
            {isEsTab ? (
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <SpeedIcon fontSize="small" />
                  الستیک سرچ
                </div>
                <div style={{ padding: 16 }}>
                  <ElasticsearchSettingsPanel />
                </div>
              </div>
            ) : isRedisTab ? (
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <StorageIcon fontSize="small" />
                  ردیس
                </div>
                <div style={{ padding: 16 }}>
                  <RedisSettingsPanel />
                </div>
              </div>
            ) : (
              <>
                {/* Non-boolean settings */}
                {nonBooleanSettings.length > 0 && (
                  <div className="settings-section-card">
                    <div className="settings-section-header">
                      <SettingsIcon fontSize="small" />
                      تنظیمات عمومی
                    </div>
                    <div className="settings-section-body">
                      {nonBooleanSettings.map(renderSettingRow)}
                    </div>
                  </div>
                )}

                {/* Boolean settings at the bottom */}
                {booleanSettings.length > 0 && (
                  <div className="settings-section-card">
                    <div className="settings-section-header">
                      <NotificationsIcon fontSize="small" />
                      سوئیچ‌های سریع
                    </div>
                    <div className="settings-switch-list">
                      {booleanSettings.map(renderSwitchRow)}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {nonBooleanSettings.length === 0 && booleanSettings.length === 0 && (
                  <div className="settings-section-card">
                    <div className="settings-empty">
                      <div className="empty-icon">
                        <SettingsApplicationsIcon style={{ fontSize: 32, color: 'var(--color-text-secondary)' }} />
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                        {searchTerm ? 'نتیجه‌ای یافت نشد' : 'تنظیماتی در این دسته وجود ندارد'}
                      </div>
                      <div style={{ fontSize: 12 }}>
                        {searchTerm
                          ? 'لطفاً عبارت جستجو را تغییر دهید یا فیلتر را پاک کنید.'
                          : 'دسته دیگری را از نوار کناری انتخاب کنید.'}
                      </div>
                      {searchTerm && (
                        <button
                          className="header-btn"
                          style={{ marginTop: 12 }}
                          onClick={() => setSearchTerm('')}
                        >
                          پاک کردن جستجو
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default SettingsManagement;
